import { MatNativeDateModule } from '@angular/material/core';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './login/login.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AuthInterceptor } from "../_infrastructure/_interceptors/auth.interceptor";
import { provideAppStore } from './store';
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { ToastrModule } from "ngx-toastr";
import { MomentDateModule } from '@angular/material-moment-adapter';
import { CurrencyPipe, DatePipe, registerLocaleData } from '@angular/common';
import { ResetPasswordComponent } from './login/reset-password/reset-password.component';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxDatePipe } from './shared/date.pipe';

import localeTa from "@angular/common/locales/ta"
import localeBn from "@angular/common/locales/bn"
import localeId from "@angular/common/locales/id"
import localeCn from "@angular/common/locales/zh-Hans"
import localeMs from "@angular/common/locales/ms"
import localeMy from "@angular/common/locales/my"
import localeEn from "@angular/common/locales/en"

// Sentry-related Imports
import { Router } from "@angular/router";
import * as Sentry from "@sentry/angular";

registerLocaleData(localeEn);
registerLocaleData(localeTa);
registerLocaleData(localeBn);
registerLocaleData(localeCn);
registerLocaleData(localeMs);
registerLocaleData(localeId);
registerLocaleData(localeMy);

/**
 * Root module: only what the login / reset-password pages and app-wide services need.
 *
 * The post-login shell (Home, Navigation, Sidebar, MemoDashboard, Notification, ...) is declared in
 * the lazy HomeModule, and AdminComponent in AdminModule, so `/login` does not download ng-bootstrap,
 * Material components, Quill, SharedModule or the push-notification module. Do not import SharedModule
 * or feature NgModules here: that would pull all of it back into the initial bundle.
 */
@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ResetPasswordComponent
  ],
  bootstrap: [AppComponent],
  imports: [BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    // App-wide Material DateAdapter. Order matters: MatNativeDateModule supplies MAT_DATE_FORMATS,
    // MomentDateModule (imported after it) overrides DateAdapter with MomentDateAdapter. Kept at root
    // because datepickers live under several top-level lazy routes (/home, /profile, /export, ...).
    MatNativeDateModule,
    MomentDateModule,
    ToastrModule.forRoot(),
    TranslateModule.forRoot({
        loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpClient]
        }
    })
  ],
  providers: [
    {
        provide: HTTP_INTERCEPTORS,
        useClass: AuthInterceptor,
        multi: true
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    }, {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    // angular-calendar services are NOT provided here: see CALENDAR_PROVIDERS in
    // shared/calendar.providers.ts (provided by HomeModule).
    CurrencyPipe,
    DatePipe,
    NgxDatePipe,
    provideHttpClient(withInterceptorsFromDi()),
    provideAppStore()
  ]
})
export class AppModule { }

// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}
